import { VALUES, IMAGES, COMPANY } from "@/data/site";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import CTASection from "@/components/CTASection";
import { useSeo } from "@/hooks/useSeo";

export default function About() {
  useSeo(
    "About StoneCrest | Exterior Cleaning Specialists in Lancashire",
    "Learn about StoneCrest Exterior Specialists, a fully insured exterior cleaning company serving homes, landlords, property managers and businesses across Lancashire.",
    "https://stonecrestexteriors.co.uk/about"
  );

  return (
    <div data-testid="about-page">
      <PageHeader
        overline="About StoneCrest"
        lines={["Professional exterior cleaning", "with a premium approach."]}
        subtitle="StoneCrest combines professional exterior cleaning with the care, communication and attention to detail you would expect from a premium property service."
      />

      <section className="px-6 md:px-12 max-w-7xl mx-auto pb-8 grid lg:grid-cols-12 gap-12 items-center">
        <Reveal className="lg:col-span-6">
          <div className="aspect-[4/3] overflow-hidden">
            <img
              src={IMAGES.about}
              alt="Property cared for by StoneCrest Exterior Specialists in Lancashire"
              className="w-full h-full object-cover"
            />
          </div>
        </Reveal>

        <div className="lg:col-span-6 space-y-5 text-[var(--muted)] leading-relaxed">
          <Reveal>
            <p>
              StoneCrest Exterior Specialists provides professional exterior
              cleaning for homeowners, landlords, property managers and
              businesses across {COMPANY.region}.
            </p>
          </Reveal>

          <Reveal delay={0.05}>
            <p>
              We provide roof cleaning, render softwashing, driveway and patio
              cleaning, gutter clearing and fascia and soffit cleaning, using
              methods selected for the surface and condition of each property.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <p>
              Every property is treated with the same care we would give our own
              home. We use professional equipment, controlled cleaning methods
              and careful preparation to achieve a high standard of finish while
              protecting the surfaces we work on.
            </p>
          </Reveal>

          <Reveal delay={0.15}>
            <p>
              From the first enquiry to the final inspection, you will deal with
              a professionally trained, fully insured team that values clear
              communication, reliability and respect for your property.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="px-6 md:px-12 max-w-7xl mx-auto py-24 md:py-32">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] text-[var(--oxblood)] mb-4">
            Our Values
          </p>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="font-serif font-light text-4xl md:text-6xl leading-tight mb-14 max-w-2xl">
            What we stand for.
          </h2>
        </Reveal>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--border)] border border-[var(--border)]">
          {VALUES.map((v, i) => (
            <Reveal
              key={v.title}
              delay={i * 0.05}
              className="bg-white p-9 hover:bg-[var(--bone)] transition-colors duration-500"
              data-testid={`value-${i}`}
            >
              <span className="font-display text-2xl text-[var(--oxblood)]/70">
                {String(i + 1).padStart(2, "0")}
              </span>

              <h3 className="font-serif text-2xl mt-3 mb-2">
                {v.title}
              </h3>

              <p className="text-sm text-[var(--muted)] leading-relaxed">
                {v.text}
              </p>
            </Reveal>
          ))}
        </div>
      </section>

      <CTASection />
    </div>
  );
}